import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Brain, 
  MessageSquare, 
  Target, 
  Heart, 
  FileText, 
  Mic, 
  TrendingUp, 
  Eye, 
  PenTool, 
  MessageCircle, 
  Users, 
  Shield, 
  GraduationCap, 
  Globe, 
  Scale, 
  BarChart3 
} from 'lucide-react';

export default function AIServicesPage() {
  const [hoveredItem, setHoveredItem] = useState(null);

  const services = [
    {
      id: 'predictive-analytics',
      title: 'Predictive Analytics',
      icon: TrendingUp,
      description: 'Uses past data to forecast future events like customer churn or market trends',
      gradient: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
      route: '/ai-services/predictive-analytics'
    },
    {
      id: 'chatbots-virtual-assistants',
      title: 'Chatbots and Virtual Assistants',
      icon: MessageSquare,
      description: 'Automates real-time customer interactions across industries',
      gradient: 'linear-gradient(135deg, #10b981, #047857)',
      route: '/ai-services/chatbots-virtual-assistants'
    },
    {
      id: 'personalized-recommendations',
      title: 'Personalized Recommendations',
      icon: Target,
      description: 'Suggests products or content based on user behavior',
      gradient: 'linear-gradient(135deg, #f59e0b, #d97706)',
      route: '/ai-services/personalized-recommendations'
    },
    {
      id: 'sentiment-analysis',
      title: 'Sentiment Analysis',
      icon: Heart,
      description: 'Analyzes text to understand public or customer emotions',
      gradient: 'linear-gradient(135deg, #ef4444, #dc2626)',
      route: '/ai-services/sentiment-analysis'
    },
    {
      id: 'document-summarization',
      title: 'Document Summarization',
      icon: FileText,
      description: 'Condenses long documents into brief summaries',
      gradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
      route: '/ai-services/document-summarization'
    },
    {
      id: 'speech-text-conversion',
      title: 'Speech-to-Text and Text-to-Speech',
      icon: Mic,
      description: 'Converts audio to text and vice versa for accessibility and automation',
      gradient: 'linear-gradient(135deg, #06b6d4, #0891b2)',
      route: '/ai-services/speech-text-conversion'
    },
    {
      id: 'demand-forecasting',
      title: 'Demand Forecasting',
      icon: BarChart3,
      description: 'Predicts product/service needs to aid inventory and staffing',
      gradient: 'linear-gradient(135deg, #14b8a6, #0f766e)',
      route: '/ai-services/demand-forecasting'
    },
    {
      id: 'vision-ai',
      title: 'Vision AI for Image and Video Processing',
      icon: Eye,
      description: 'Processes images/videos for diagnostics, ads, or quality control',
      gradient: 'linear-gradient(135deg, #ec4899, #be185d)',
      route: '/ai-services/vision-ai'
    },
    {
      id: 'content-creation',
      title: 'Intelligent Content Creation',
      icon: PenTool,
      description: 'Generates content like blogs, scripts, and ad copy',
      gradient: 'linear-gradient(135deg, #6366f1, #4f46e5)',
      route: '/ai-services/content-creation'
    },
    {
      id: 'natural-language-generation',
      title: 'Natural Language Generation (NLG)',
      icon: MessageCircle,
      description: 'Creates human-like text for reports and summaries',
      gradient: 'linear-gradient(135deg, #84cc16, #65a30d)',
      route: '/ai-services/natural-language-generation'
    },
    {
      id: 'customer-behavior-modeling',
      title: 'Customer Behavior Modeling',
      icon: Users,
      description: 'Analyzes customer actions to improve engagement and marketing',
      gradient: 'linear-gradient(135deg, #f97316, #ea580c)',
      route: '/ai-services/customer-behavior-modeling'
    },
    {
      id: 'fraud-detection',
      title: 'Fraud Detection',
      icon: Shield,
      description: 'Identifies suspicious activity to prevent fraud',
      gradient: 'linear-gradient(135deg, #dc2626, #b91c1c)',
      route: '/ai-services/fraud-detection'
    },
    {
      id: 'adaptive-learning',
      title: 'Adaptive Learning Platforms',
      icon: GraduationCap,
      description: 'Provides personalized learning paths for users or employees',
      gradient: 'linear-gradient(135deg, #2563eb, #1e40af)',
      route: '/ai-services/adaptive-learning'
    },
    {
      id: 'multi-lingual-translation',
      title: 'Multi-Lingual Translation',
      icon: Globe,
      description: 'Translates content to serve a global audience',
      gradient: 'linear-gradient(135deg, #059669, #047857)',
      route: '/ai-services/multi-lingual-translation'
    },
    {
      id: 'legal-document-analysis',
      title: 'Legal Document Analysis',
      icon: Scale,
      description: 'Extracts insights from complex legal texts',
      gradient: 'linear-gradient(135deg, #7c2d12, #92400e)',
      route: '/ai-services/legal-document-analysis'
    },
    {
      id: 'trend-pattern-recognition',
      title: 'Trend & Pattern Recognition',
      icon: BarChart3,
      description: 'Identifies trends in data for better decision-making',
      gradient: 'linear-gradient(135deg, #0f172a, #374151)',
      route: '/ai-services/trend-pattern-recognition'
    }
  ];

  const containerStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f0f9ff, #e0f2fe)',
    padding: '80px 16px',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '64px',
    maxWidth: '1200px',
    margin: '0 auto 64px auto'
  };

  const iconContainerStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '80px',
    height: '80px',
    background: 'linear-gradient(135deg, #3b82f6, #1e40af)',
    borderRadius: '50%',
    marginBottom: '24px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
  };

  const titleStyle = {
    fontSize: '3.5rem',
    fontWeight: '900',
    background: 'linear-gradient(135deg, #1f2937, #3b82f6, #1e40af)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    marginBottom: '24px',
    lineHeight: '1.1'
  };

  const subtitleStyle = {
    fontSize: '1.25rem',
    color: '#4b5563',
    maxWidth: '700px',
    margin: '0 auto',
    lineHeight: '1.6'
  };

  const dividerStyle = {
    width: '96px',
    height: '4px',
    background: 'linear-gradient(135deg, #3b82f6, #1e40af)',
    margin: '32px auto 0 auto',
    borderRadius: '2px'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '32px',
    maxWidth: '1400px',
    margin: '0 auto'
  };

  const getCardStyle = (serviceId) => ({
    position: 'relative',
    height: '100%',
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '16px',
    padding: '32px',
    textDecoration: 'none',
    color: 'inherit',
    display: 'block',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    transform: hoveredItem === serviceId ? 'scale(1.05)' : 'scale(1)',
    backgroundColor: hoveredItem === serviceId ? '#f8fafc' : '#ffffff',
    borderColor: hoveredItem === serviceId ? '#cbd5e1' : '#e5e7eb'
  });

  const getIconStyle = (gradient) => ({
    width: '64px',
    height: '64px',
    background: gradient,
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '24px',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    transform: hoveredItem ? 'scale(1.1)' : 'scale(1)',
    transition: 'transform 0.3s ease'
  });

  const serviceTitleStyle = {
    fontSize: '1.25rem',
    fontWeight: '700',
    color: '#1f2937',
    marginBottom: '12px'
  };

  const serviceDescStyle = {
    fontSize: '0.875rem',
    color: '#6b7280',
    lineHeight: '1.5',
    marginBottom: '24px'
  };

  const learnMoreStyle = {
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#3b82f6'
  };

  const ctaContainerStyle = {
    textAlign: 'center',
    marginTop: '80px',
    maxWidth: '1200px',
    margin: '80px auto 0 auto'
  };

  const ctaBoxStyle = {
    background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
    border: '1px solid #e5e7eb',
    borderRadius: '16px',
    padding: '48px 32px',
    maxWidth: '600px',
    margin: '0 auto',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
  };

  const ctaTitleStyle = {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#1f2937',
    marginBottom: '16px'
  };

  const ctaTextStyle = {
    fontSize: '1.125rem',
    color: '#4b5563',
    marginBottom: '32px'
  };

  const buttonStyle = {
    background: 'linear-gradient(135deg, #3b82f6, #1e40af)',
    color: '#ffffff',
    fontWeight: '700',
    padding: '16px 32px',
    borderRadius: '12px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1rem',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s ease',
    textDecoration: 'none',
    display: 'inline-block'
  };

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <div style={iconContainerStyle}>
          <Brain style={{ width: '40px', height: '40px', color: '#ffffff' }} />
        </div>
        <h1 style={titleStyle}>
          Artificial Intelligence<br />
          <span style={{ fontSize: '2.5rem' }}>Services</span>
        </h1>
        <p style={subtitleStyle}>
          Harness the power of artificial intelligence to transform your business operations with intelligent solutions that streamline processes and drive strategic growth
        </p>
        <div style={dividerStyle}></div>
      </div>

      <div style={gridStyle}>
        {services.map((service, index) => {
          const Icon = service.icon;
          return (
            <Link
              to={service.route}
              key={service.id}
              style={getCardStyle(service.id)}
              onMouseEnter={() => setHoveredItem(service.id)}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <div style={getIconStyle(service.gradient)}>
                <Icon style={{ width: '32px', height: '32px', color: '#ffffff' }} />
              </div>

              <div>
                <h3 style={serviceTitleStyle}>
                  {service.title}
                </h3>
                <p style={serviceDescStyle}>
                  {service.description}
                </p>
                
                <div style={learnMoreStyle}>
                  <span>Learn More</span>
                  <svg style={{ width: '16px', height: '16px', marginLeft: '8px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      <div style={ctaContainerStyle}>
        <div style={ctaBoxStyle}>
          <h2 style={ctaTitleStyle}>
            Ready to Transform Your Business with AI?
          </h2>
          <p style={ctaTextStyle}>
            Discover how our intelligent solutions can streamline your operations and drive growth.
          </p>
          <Link to="/contact" style={buttonStyle}>
            GET STARTED →
          </Link>
        </div>
      </div>
    </div>
  );
}